import { useRef, useEffect } from "react";
import { CONTENT_H, CONTENT_W, clamp, hash01, lerp, randInt } from "./chunk-0.jsx";
import { styleForIrisEyes } from "./chunk-18.jsx";
import { drawIrisEye } from "./chunk-20.jsx";
export function RoundLcdEyes({
  emotion,
  variant,
  poke,
  heartbeatRef,
  accent,
  active = true,
  motionBoost = false
}) {
  const canvasRef = useRef(null);
  const emotionRef = useRef(emotion);
  const accentRef = useRef(accent);
  const pokeRef = useRef(poke);
  useEffect(() => {
    emotionRef.current = emotion;
  }, [emotion]);
  useEffect(() => {
    accentRef.current = accent;
  }, [accent]);
  useEffect(() => {
    pokeRef.current = poke;
  }, [poke]);
  const stateRef = useRef({
    gazeX: 0,
    gazeY: 0,
    tgtX: 0,
    tgtY: 0,
    nextGazeAt: 0,
    blink: 0,
    blinkDir: 0,
    nextBlinkAt: 0,
    lastPoke: 0,
    lastT: 0
  });
  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = true;
    const s = stateRef.current;
    s.nextBlinkAt = Date.now() + 900;
    s.nextGazeAt = Date.now() + 350;
    let raf = 0;
    const loop = t => {
      heartbeatRef.current = Date.now();
      const now = Date.now();
      const dt = s.lastT ? now - s.lastT : 16;
      s.lastT = now;
      const v = String(variant || '').toLowerCase();
      const seed = hash01(`round:${v}`);
      const gazeLerp = motionBoost ? 0.06 : 0.035;
      const gazeAmpX = motionBoost ? 0.95 : 0.8;
      const gazeAmpY = motionBoost ? 0.7 : 0.55;
      if (!s.nextGazeAt || now >= s.nextGazeAt) {
        s.tgtX = (Math.random() * 2 - 1) * gazeAmpX;
        s.tgtY = (Math.random() * 2 - 1) * gazeAmpY;
        s.nextGazeAt = now + (motionBoost ? 900 : 1400) + randInt(motionBoost ? 1400 : 2200);
      }
      s.gazeX = lerp(s.gazeX, s.tgtX, gazeLerp);
      s.gazeY = lerp(s.gazeY, s.tgtY, gazeLerp);
      if (motionBoost) {
        const driftX = Math.sin(t / 980 + seed * 9) * 0.1;
        const driftY = Math.sin(t / 820 + seed * 5 + 0.9) * 0.07;
        s.gazeX = clamp(s.gazeX + driftX, -1, 1);
        s.gazeY = clamp(s.gazeY + driftY, -1, 1);
      }
      const pokeNow = pokeRef.current || 0;
      if (pokeNow !== s.lastPoke) {
        s.lastPoke = pokeNow;
        s.blinkDir = 1;
        s.nextBlinkAt = now + (motionBoost ? 1500 : 2200) + randInt(motionBoost ? 1700 : 2800);
      }
      if (s.blinkDir === 0 && now >= s.nextBlinkAt) s.blinkDir = 1;
      if (s.blinkDir !== 0) {
        s.blink = clamp(s.blink + s.blinkDir * dt / (motionBoost ? 80 : 100), 0, 1);
        if (s.blinkDir === 1 && s.blink >= 1) s.blinkDir = -1;
        if (s.blinkDir === -1 && s.blink <= 0) {
          s.blinkDir = 0;
          s.nextBlinkAt = now + (motionBoost ? 1800 : 2500) + randInt(motionBoost ? 2400 : 4000);
        }
      }
      const style = styleForIrisEyes(emotionRef.current);
      const openness = clamp(style.openness - s.blink * 1.1, 0, 1.2);
      const pupilScale = clamp(style.pupil + Math.sin(t / (motionBoost ? 760 : 900)) * (motionBoost ? 0.075 : 0.05), 0.35, 1.35);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const rimR = Math.min(canvas.width, canvas.height) * 0.46;
      const rimCx = canvas.width / 2;
      const rimCy = canvas.height / 2;
      ctx.save();
      ctx.fillStyle = '#0b1220';
      ctx.beginPath();
      ctx.arc(rimCx, rimCy, rimR + 8, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#020617';
      ctx.beginPath();
      ctx.arc(rimCx, rimCy, rimR + 2, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.arc(rimCx, rimCy, rimR, 0, Math.PI * 2);
      ctx.clip();
      const irisHue = (() => {
        const v = accentRef.current;
        if (typeof v === 'string' && v.startsWith('#') && (v.length === 7 || v.length === 4)) return v;
        return '#38bdf8';
      })();
      const eyeR = rimR * 0.3;
      const cxL = rimCx - rimR * 0.32;
      const cxR = rimCx + rimR * 0.32;
      const cy = rimCy + rimR * 0.03;
      drawIrisEye(ctx, cxL, cy, eyeR, {
        openness,
        pupilScale,
        gazeX: s.gazeX,
        gazeY: s.gazeY,
        iris: irisHue,
        brow: style.brow * 0.6,
        side: 'left'
      });
      drawIrisEye(ctx, cxR, cy, eyeR, {
        openness,
        pupilScale,
        gazeX: s.gazeX,
        gazeY: s.gazeY,
        iris: irisHue,
        brow: style.brow * 0.6,
        side: 'right'
      });
      ctx.restore();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [active, heartbeatRef, variant, motionBoost]);
  return <div className="w-full h-full flex items-center justify-center">
      <canvas ref={canvasRef} width={CONTENT_W} height={CONTENT_H} style={{
      width: '100%',
      height: '100%'
    }} />
    </div>;
}
